/* eslint-disable import/no-unused-modules */

import type { ColumnDef, SurchargeConfig } from './type/types';

export const PRIMARY_BUTTON_COLOR = '#D87F5C';

/**
 * All possible product table columns.
 * Columns are conditionally shown based on whether the proposal has discounts / mixed tax rates.
 */
export const PRODUCT_TABLE_COLUMNS = [
    { key: 'title', header: 'Products', className: 'flex-1' },
    { key: 'qty', header: 'QTY', className: 'w-[3rem] text-right' },
    { key: 'qtySpace', header: '', className: 'w-[5.6rem]' },
    { key: 'unitPriceSpace', header: '', className: 'w-[6rem]' },
    {
        key: 'unitPrice',
        header: 'UNIT PRICE',
        className: 'w-[7rem] text-left',
    },
    {
        key: 'discount',
        header: 'DISCOUNT',
        className: 'w-[11rem] text-right',
    },
    { key: 'tax', header: 'TAX', className: 'w-[8rem] text-right' },
    {
        key: 'price',
        header: 'PRICE',
        className: 'w-[10.95rem] text-right md:pr-[1.62rem] print:pr-[1.62rem]',
    },
    // { key: 'priceSpace', header: '', className: 'w-[1.0625rem]' },
] as const;

/**
 * Payment table columns (for both quote and invoice, some columns may be invisible for quote or invoice):
 * title (header: "Scheduled Payments")
 * status (header: "STATUS", value always "Unpaid")
 * due (header: "DUE", eg "20.08.2025" for NZ/AU, "08.20.2025" for US)
 * paid on (header: "PAID ON", value always "-")
 * amount (header: "" eg "$ 8,734.00")
 * Pay Button (header: "", button text "Pay" or "Paid")
 */

export const QUOTE_COLUMNS: ColumnDef[] = [
    {
        key: 'title',
        header: 'Scheduled Payments',
        className: 'w-[5.33rem] md:pr-[1rem] print:pr-[1rem]',
    },
    {
        key: 'status',
        header: '',
        className: 'flex-1 md:pl-[0.0625rem] print:pl-[0.0625rem]',
    },
    {
        key: 'due',
        header: 'DUE',
        className: 'w-[6rem] md:pl-[0rem] print:pl-[0rem]',
    },
    {
        key: 'paidOn',
        header: 'PAID ON',
        className: 'w-[6.5rem] md:pl-[0.4rem] print:pl-[0.4rem]',
    },
    {
        key: 'amount',
        header: 'AMOUNT',
        className: 'w-[5.26rem] text-right md:pr-[0.63rem] print:pr-[0.63rem]',
    },
];

/** PDF-optimised quote columns: wider title, amount has room for full price */
export const PDF_QUOTE_COLUMNS: ColumnDef[] = [
    {
        key: 'title',
        header: 'Scheduled Payments',
        className: 'w-[5.33rem] pr-[1rem]',
    },
    {
        key: 'status',
        header: '',
        className: 'flex-1 pl-[0.0625rem]',
    },
    {
        key: 'due',
        header: 'DUE',
        className: 'w-[6.5rem]',
    },
    {
        key: 'paidOn',
        header: 'PAID ON',
        className: 'w-[6.5rem] pl-[0.4rem]',
    },
    {
        key: 'amount',
        header: 'AMOUNT',
        className: 'w-[8rem] text-right pr-[0.63rem]',
    },
];

export const INVOICE_COLUMNS: ColumnDef[] = [
    {
        key: 'title',
        header: 'Scheduled Payments',
        className: 'w-[5.33rem] md:pr-[1rem] print:pr-[1rem]',
    },
    {
        key: 'status',
        header: '',
        className: 'flex-1 md:pl-[0.0625rem] print:pl-[0.0625rem]',
    },
    {
        key: 'due',
        header: 'DUE',
        className: 'w-[6rem] md:pl-[0rem] print:pl-[0rem]',
    },
    {
        key: 'paidOn',
        header: 'PAID ON',
        className: 'w-[6.5rem] md:pl-[0.4rem] print:pl-[0.4rem]',
    },
    {
        key: 'amount',
        header: 'AMOUNT',
        className: 'w-[5.26rem] text-right md:pr-[0.63rem] print:pr-[0.63rem]',
    },
    {
        key: 'payButton',
        header: '',
        className:
            'w-[11.2rem] flex justify-end md:pr-[1.3rem] print:pr-[1.3rem]',
    },
];

/** PDF-optimised invoice columns: wider title, amount has room for full price */
export const PDF_INVOICE_COLUMNS: ColumnDef[] = [
    {
        key: 'title',
        header: 'Scheduled Payments',
        className: 'w-[5.33rem] pr-[1rem]',
    },
    {
        key: 'status',
        header: '',
        className: 'flex-1 pl-[0.0625rem]',
    },
    {
        key: 'due',
        header: 'DUE',
        className: 'w-[6.5rem]',
    },
    {
        key: 'paidOn',
        header: 'PAID ON',
        className: 'w-[6.5rem] pl-[0.4rem]',
    },
    {
        key: 'amount',
        header: 'AMOUNT',
        className: 'w-[8rem] text-right pr-[0.63rem]',
    },
    {
        key: 'payButton',
        header: '',
        className: 'w-[11.2rem] flex justify-end pr-[1.3rem]',
    },
];

export const SURCHARGE_CONFIG_NZ: SurchargeConfig = {
    rate: 0.027,
    flatFee: 0,
};

export const SURCHARGE_CONFIG_AU: SurchargeConfig = {
    rate: 0.027,
    flatFee: 0,
};

export const SURCHARGE_CONFIG_US: SurchargeConfig = {
    rate: 0.027,
    flatFee: 0,
};

export const SURCHARGE_CONFIG_DEFAULT: SurchargeConfig = {
    rate: 0,
    flatFee: 0,
};
